import React, { useMemo, useRef, useState } from 'react';
import Constants from 'expo-constants';
import { usePathname } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { ImagePlus, Star, X } from 'lucide-react';
import {
  FEEDBACK_CATEGORIES,
  type FeedbackCategory,
} from '@tabbio/types';
import { colors } from '@tabbio/tokens';
import { FILE_UPLOAD } from '@tabbio/utils';
import { useLocale } from '../../hooks/useLocale';
import { useTheme } from '../../hooks/useTheme';
import { useToast } from '../../hooks/useToast';
import { submitFeedback } from '../../lib/feedback-api';
import {
  fromWebFile,
  ImageUploadError,
  uploadImageAsset,
} from '../../lib/image-upload';
import { trackProductEvent } from '../../lib/product-analytics';
import { WebDialog } from '../common/WebDialog.web';
import { FeedbackIcon } from '../icons/sidebar-icons';

const CATEGORY_KEYS: FeedbackCategory[] = [...FEEDBACK_CATEGORIES];
const DEFAULT_CATEGORY: FeedbackCategory = 'idea';
const DEFAULT_RATING = 5;
const RATING_STAR_COLOR = colors.warning;
const MIN_MESSAGE_LENGTH = 10;
const MAX_MESSAGE_LENGTH = 2000;

function getAppVersion() {
  return Constants.expoConfig?.version ?? null;
}

function getViewportMetadata() {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return {
    width: Math.round(window.innerWidth),
    height: Math.round(window.innerHeight),
  };
}

type FeedbackFloatingButtonProps = {
  compact?: boolean;
  renderTrigger?: (open: () => void) => React.ReactNode;
};

const hoverIn = (event: React.MouseEvent<HTMLButtonElement>) => {
  event.currentTarget.style.backgroundColor = 'var(--hover-strong)';
};

const hoverOut = (event: React.MouseEvent<HTMLButtonElement>) => {
  event.currentTarget.style.backgroundColor = 'transparent';
  event.currentTarget.style.transform = 'scale(1)';
};

export function FeedbackFloatingButton({ compact = false, renderTrigger }: FeedbackFloatingButtonProps) {
  const { theme } = useTheme();
  const { t } = useTranslation('common');
  const toast = useToast();
  const pathname = usePathname();
  const { locale } = useLocale();
  const [visible, setVisible] = useState(false);
  const [category, setCategory] = useState<FeedbackCategory>(DEFAULT_CATEGORY);
  const [rating, setRating] = useState(DEFAULT_RATING);
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const appVersion = useMemo(() => getAppVersion(), []);
  const canSubmit = message.trim().length >= MIN_MESSAGE_LENGTH;
  const atMaxLength = message.length >= MAX_MESSAGE_LENGTH;

  const resolveScreenshotError = (uploadError: unknown): string => {
    if (uploadError instanceof ImageUploadError) {
      if (uploadError.code === 'unsupported-type') {
        return t('feedback.screenshotErrors.unsupportedType');
      }
      if (uploadError.code === 'too-large') {
        return t('feedback.screenshotErrors.tooLarge', {
          size: FILE_UPLOAD.maxSizeMB,
        });
      }
    }
    return t('feedback.screenshotError');
  };

  const resetForm = () => {
    setCategory(DEFAULT_CATEGORY);
    setRating(DEFAULT_RATING);
    setMessage('');
    setError(null);
    setScreenshotUrl(null);
  };

  const handleScreenshotChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      setUploadingScreenshot(true);
      const asset = fromWebFile(file);
      const url = await uploadImageAsset(asset, {
        kind: 'feedback-screenshot',
        fileName: asset.name,
        mimeType: asset.type,
      });
      setScreenshotUrl(url);
    } catch (uploadError) {
      toast.error(resolveScreenshotError(uploadError));
    } finally {
      setUploadingScreenshot(false);
    }
  };

  const close = () => {
    if (submitting) return;
    setVisible(false);
    setError(null);
  };

  const handleSubmit = async () => {
    const trimmedMessage = message.trim();
    if (trimmedMessage.length < MIN_MESSAGE_LENGTH) {
      setError(t('feedback.validation.messageMin'));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await submitFeedback({
        category,
        rating,
        message: trimmedMessage,
        route: pathname || undefined,
        platform: 'web',
        appVersion: appVersion ?? undefined,
        locale,
        screenshotUrl: screenshotUrl ?? undefined,
        metadata: getViewportMetadata(),
      });

      trackProductEvent('feedback_submitted', {
        category,
        rating,
        platform: 'web',
        route: pathname || null,
      });
      toast.success(t('feedback.successTitle'), t('feedback.successMessage'));
      resetForm();
      setVisible(false);
    } catch (submitError) {
      const messageText =
        submitError instanceof Error
          ? submitError.message
          : t('feedback.errorMessage');
      setError(messageText);
      toast.error(t('feedback.errorTitle'), messageText);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger(() => setVisible(true))
      ) : (
        <button
          type="button"
          title={t('feedback.floatingLabel')}
          aria-label={t('feedback.floatingLabel')}
          aria-expanded={visible}
          aria-haspopup="dialog"
          tabIndex={visible ? -1 : 0}
          onClick={() => {
            if (!visible) setVisible(true);
          }}
          className="tabbio-nav-item tabbio-feedback-nav-item"
          style={{
            color: 'var(--text-primary)',
          }}
          onMouseEnter={hoverIn}
          onMouseLeave={hoverOut}
          onMouseDown={(event) => {
            if (!visible) {
              event.currentTarget.style.transform = 'scale(0.97)';
            }
          }}
          onMouseUp={(event) => {
            event.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              fontSize: 20,
              lineHeight: 1,
            }}
          >
            <FeedbackIcon />
          </span>
          {!compact ? (
            <span className="tabbio-nav-label">
              {t('feedback.floatingLabel')}
            </span>
          ) : null}
        </button>
      )}

      <WebDialog
        visible={visible}
        title={t('feedback.title')}
        onClose={close}
        maxWidth={560}
        footer={
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(112px, 0.7fr) minmax(180px, 1.35fr)',
              gap: 12,
              alignItems: 'center',
            }}
          >
            <button
              type="button"
              onClick={close}
              disabled={submitting}
              style={{
                height: 48,
                borderRadius: 9999,
                border: 'none',
                background: 'transparent',
                color: theme.text,
                fontSize: 15,
                fontWeight: 650,
                cursor: submitting ? 'default' : 'pointer',
              }}
            >
              {t('actions.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || !canSubmit}
              style={{
                height: 48,
                borderRadius: 9999,
                border: 'none',
                background: colors.primary,
                color: '#FFFFFF',
                fontSize: 15,
                fontWeight: 700,
                cursor: submitting || !canSubmit ? 'default' : 'pointer',
                opacity: submitting || !canSubmit ? 0.45 : 1,
              }}
            >
              {submitting ? t('feedback.submitting') : t('feedback.submit')}
            </button>
          </div>
        }
      >
        <div style={{ display: 'grid', gap: 22 }}>
          <section style={{ display: 'grid', gap: 12 }}>
            <h3 style={{ margin: 0, fontSize: 18, lineHeight: 1.2, fontWeight: 750, color: theme.text }}>
              {t('feedback.categoryLabel')}
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {CATEGORY_KEYS.map((key) => {
                const active = category === key;
                return (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setCategory(key)}
                    style={{
                      minHeight: 44,
                      borderRadius: 9999,
                      border: `1px solid ${active ? colors.primary : theme.borderSubtle}`,
                      background: active ? colors.primary : theme.background,
                      color: active ? '#FFFFFF' : theme.textSecondary,
                      padding: '0 18px',
                      fontSize: 15,
                      fontWeight: 650,
                      cursor: 'pointer',
                    }}
                  >
                    {t(`feedback.categories.${key}`)}
                  </button>
                );
              })}
            </div>
          </section>

          <section style={{ display: 'grid', gap: 12 }}>
            <h3 style={{ margin: 0, fontSize: 18, lineHeight: 1.2, fontWeight: 750, color: theme.text }}>
              {t('feedback.ratingLabel')}
            </h3>
            <div style={{ display: 'flex', gap: 12 }}>
              {[1, 2, 3, 4, 5].map((value) => {
                const active = value <= rating;
                return (
                  <button
                    key={value}
                    type="button"
                    aria-label={t('feedback.ratingOption', { value })}
                    aria-pressed={active}
                    onClick={() => setRating(value)}
                    style={{
                      width: 44,
                      height: 44,
                      border: 'none',
                      background: 'transparent',
                      color: active ? RATING_STAR_COLOR : theme.textTertiary,
                      display: 'grid',
                      placeItems: 'center',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    <Star
                      size={36}
                      strokeWidth={1.9}
                      fill={active ? RATING_STAR_COLOR : 'transparent'}
                    />
                  </button>
                );
              })}
            </div>
          </section>

          <label style={{ display: 'grid', gap: 12 }}>
            <span style={{ fontSize: 18, lineHeight: 1.2, fontWeight: 750, color: theme.text }}>
              {t('feedback.messageLabel')}
            </span>
            <textarea
              value={message}
              onChange={(event) => {
                setMessage(event.target.value);
                if (error) setError(null);
              }}
              maxLength={MAX_MESSAGE_LENGTH}
              placeholder={t(`feedback.placeholders.${category}`, {
                defaultValue: t('feedback.messagePlaceholder'),
              })}
              style={{
                minHeight: 190,
                resize: 'vertical',
                borderRadius: 22,
                border: `1px solid ${
                  error ? colors.danger : atMaxLength ? colors.danger : theme.border
                }`,
                background: theme.background,
                color: theme.text,
                padding: '16px 18px',
                fontSize: 16,
                lineHeight: 1.55,
                outline: 'none',
              }}
            />
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 12,
                fontSize: 13,
                lineHeight: 1.45,
              }}
            >
              <span
                style={{
                  color: error
                    ? colors.danger
                    : atMaxLength
                      ? colors.danger
                      : theme.textTertiary,
                }}
              >
                {error ??
                  (atMaxLength
                    ? t('feedback.validation.messageMax', {
                        max: MAX_MESSAGE_LENGTH,
                      })
                    : t(`feedback.helpers.${category}`, {
                        defaultValue: t('feedback.messageHelper'),
                      }))}
              </span>
              <span
                style={{
                  flexShrink: 0,
                  color: atMaxLength ? colors.danger : theme.textTertiary,
                }}
              >
                {t('feedback.charCount', {
                  count: message.length,
                  max: MAX_MESSAGE_LENGTH,
                })}
              </span>
            </div>
          </label>

          <section style={{ display: 'grid', gap: 12 }}>
            <h3 style={{ margin: 0, fontSize: 18, lineHeight: 1.2, fontWeight: 750, color: theme.text }}>
              {t('feedback.screenshotLabel')}
            </h3>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(event) => void handleScreenshotChange(event)}
              style={{ display: 'none' }}
            />
            {screenshotUrl ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <img
                  src={screenshotUrl}
                  alt=""
                  style={{
                    width: 120,
                    height: 80,
                    objectFit: 'cover',
                    borderRadius: 12,
                    border: `1px solid ${theme.borderSubtle}`,
                  }}
                />
                <button
                  type="button"
                  onClick={() => setScreenshotUrl(null)}
                  aria-label={t('feedback.removeScreenshot')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: 9999,
                    border: `1px solid ${theme.borderSubtle}`,
                    background: 'transparent',
                    color: theme.text,
                    cursor: 'pointer',
                  }}
                >
                  <X size={18} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingScreenshot}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  alignSelf: 'flex-start',
                  justifySelf: 'flex-start',
                  borderRadius: 9999,
                  border: `1px solid ${theme.border}`,
                  background: 'transparent',
                  color: theme.textSecondary,
                  padding: '10px 16px',
                  fontSize: 15,
                  fontWeight: 650,
                  cursor: uploadingScreenshot ? 'default' : 'pointer',
                  opacity: uploadingScreenshot ? 0.6 : 1,
                }}
              >
                <ImagePlus size={18} />
                {uploadingScreenshot
                  ? t('feedback.screenshotUploading')
                  : t('feedback.addScreenshot')}
              </button>
            )}
          </section>
        </div>
      </WebDialog>
    </>
  );
}
